/**
 * SDR ReachOut  API client
 * All calls go to the Express server (proxied via package.json "proxy" in dev,
 * or same origin in production when served by Express).
 */

const BASE = "/api";

async function request(path, method = "GET", body = null) {
  const token = localStorage.getItem("sdr_token");
  const opts = {
    method,
    headers: { "Content-Type": "application/json", ...(token?{"Authorization":"Bearer "+token}:{}) },
  };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${BASE}${path}`, opts);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

//  User Settings (per-user)
export const getUserSettings = () => request("/user/settings");
export const updateUserSettings = (s) => request("/user/settings", "PUT", s);
export const getUserOutlookStatus = () => request("/user/outlook/status");
export const connectUserOutlook = (cfg) => request("/user/outlook/connect", "POST", cfg);

//  Account
export const deleteAccount = (d) => request("/account/delete", "POST", d);

//  Settings 
export const getSettings    = ()  => request("/settings");
export const updateSettings = (s) => request("/settings", "PUT", s);

//  Outlook 
export const getOutlookStatus  = ()    => request("/outlook/status");
export const connectOutlook    = (cfg) => request("/outlook/connect",    "POST", cfg);
export const disconnectOutlook = ()    => request("/outlook/disconnect", "POST");
export const sendEmail         = (d)   => request("/outlook/send-email", "POST", d);
export const fetchInbox        = ()    => request("/outlook/inbox");
export const bookMeeting       = (d)   => request("/outlook/book-meeting", "POST", d);

//  Leads 
export const getLeads    = (q = {}) => request("/leads?" + new URLSearchParams(q));
export const getLead     = (id)     => request(`/leads/${id}`);
export const createLead  = (d)      => request("/leads", "POST", d);
export const updateLead  = (id, d)  => request(`/leads/${id}`, "PUT", d);
export const deleteLead  = (id)     => request(`/leads/${id}`, "DELETE");

//  Campaigns 
export const getCampaigns    = ()       => request("/campaigns");
export const createCampaign  = (d)      => request("/campaigns", "POST", d);
export const updateCampaign  = (id, d)  => request(`/campaigns/${id}`, "PUT", d);
export const deleteCampaign  = (id)     => request(`/campaigns/${id}`, "DELETE");
export const runCampaign     = (id)     => request(`/campaigns/${id}/run`, "POST");

//  Replies 
export const getReplies   = ()     => request("/replies");
export const createReply  = (d)    => request("/replies", "POST", d);
export const deleteReply  = (id)   => request(`/replies/${id}`, "DELETE");

//  Meetings 
export const getMeetings    = ()      => request("/meetings");
export const createMeeting  = (d)     => request("/meetings", "POST", d);
export const updateMeeting  = (id, d) => request(`/meetings/${id}`, "PUT", d);
export const deleteMeeting  = (id)    => request(`/meetings/${id}`, "DELETE");

//  Deals / Pipeline
export const getDeals        = ()      => request("/deals");
export const getDealsSummary = ()      => request("/deals/summary");
export const createDeal      = (d)     => request("/deals", "POST", d);
export const updateDeal      = (id, d) => request(`/deals/${id}`, "PUT", d);
export const deleteDeal      = (id)    => request(`/deals/${id}`, "DELETE");

//  Team
export const getTeam         = ()      => request("/team");
export const createTeamMember= (d)     => request("/team", "POST", d);
export const updateTeamMember= (id, d) => request(`/team/${id}`, "PUT", d);
export const deleteTeamMember= (id)    => request(`/team/${id}`, "DELETE");

//  Templates (shared repository — Mail Merge, Sequences, Workspace)
export const getTemplates    = (category) => request("/templates" + (category ? "?category=" + encodeURIComponent(category) : ""));
export const createTemplate  = (d)        => request("/templates", "POST", d);
export const updateTemplate  = (id, d)    => request(`/templates/${id}`, "PUT", d);
export const deleteTemplate  = (id)       => request(`/templates/${id}`, "DELETE");
export const useTemplate     = (id)       => request(`/templates/${id}/use`, "POST").catch(() => {}); // fire-and-forget usage counter

//  Stats
export const getStats = () => request("/stats");
export const getSequences = () => request("/sequences");

//  AI 
export const genSequence      = (lead, settings) => request("/ai/sequence",        "POST", { lead, settings });
export const genReplyDraft    = (reply, settings) => request("/ai/draft-reply",    "POST", { reply, settings });
export const genLinkedIn      = (persona, hook, settings) => request("/ai/linkedin", "POST", { persona, hook, settings });
export const classifyIntent   = (body)            => request("/ai/classify-intent","POST", { body });
export const getMeetingBrief  = (meetingId)       => request("/ai/meeting-brief",  "POST", { meetingId });
export const voiceDraft       = (payload)         => request("/ai/voice-draft",    "POST", payload);

export const apolloSearch = (params) =>
  fetch('/api/apollo/search', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params)
  }).then(r => r.json());

//  7-Day Free Trial signup + onboarding
export const trialSignup        = (d)  => request("/auth/trial-signup", "POST", d);
export const resendVerification = ()   => request("/auth/resend-verification", "POST");
export const getTrialStatus     = ()   => request("/auth/trial-status");
export const submitOnboarding   = (d)  => request("/onboarding/personalize", "POST", d);
export const getSmtpAccounts    = ()   => request("/smtp/accounts");
